import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AppButton } from '@/components/AppButton'
import { useCreateSavingGoal, useUpdateSavingGoal } from '@/domain/transfer'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useAppAnalytics } from '@/services/analytics'

export const SetGoalButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goal = useSavingsStore((s) => s.goal)
  const savedGoalId = useSavingsStore((s) => s.savedGoalId)
  const setGoalTransferIn = useSavingsStore((s) => s.setGoalTransferIn)
  const resetGoal = useSavingsStore((s) => s.resetGoal)
  const setSavingGoalId = useSavingsStore((s) => s.setSavingGoalId)
  const { trackEvent, events } = useAppAnalytics()

  const { mutateAsync: createSavingGoal, isPending: isCreatingSavingGoal } =
    useCreateSavingGoal({
      onSuccess: (data) => {
        setGoalTransferIn({
          saving_goal_id: data.id || '',
          category: data.category || '',
        })

        // Track savings_opened
        const amount = Number(goal.cryptoAmount.amount)
        trackEvent(events.SAVINGS_OPENED, {
          initial_deposit_amount: amount,
        })

        // Навигация с заменой текущей записи в истории, чтобы избежать промежуточных редиректов
        navigate('/savings/first-deposit', { replace: true })

        // Сбрасываем состояние цели после навигации
        setTimeout(() => resetGoal(), 100)
      },
      onError: ({ errorMessage }) => {
        alert(errorMessage || 'Unknown error')
      },
    })

  const { mutateAsync: updateSavingGoal, isPending: isUpdatingSavingGoal } =
    useUpdateSavingGoal({
      onSuccess: () => {
        const goalIdToNavigate = savedGoalId // Сохраняем ID перед сбросом

        // Сначала навигируемся, затем сбрасываем состояние
        navigate(`/savings/savings-detail/${goalIdToNavigate}`, {
          replace: true,
        })

        // Сбрасываем состояние с задержкой, чтобы избежать конфликта с проверкой в SetGoalPage
        setTimeout(() => {
          resetGoal()
          setSavingGoalId('')
        }, 100)
      },
      onError: ({ errorMessage }) => {
        alert(errorMessage || 'Unknown error')
      },
    })

  const handleSaveGoal = async () => {
    // Basic validation
    const amount = Number(goal.cryptoAmount.amount)
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount')
      return
    }

    if (!goal.category || !goal.name) {
      alert('Please select a goal category')
      return
    }

    if (savedGoalId) {
      return updateSavingGoal({
        category: goal.category,
        name: goal.name,
        goal_amount: amount,
        saving_goal_id: savedGoalId,
      })
    }

    createSavingGoal({
      category: goal.category,
      name: goal.name,
      goal_amount: amount,
    })
  }

  return (
    <AppButton
      type="primaryGradient"
      onPress={handleSaveGoal}
      isLoading={isCreatingSavingGoal || isUpdatingSavingGoal}
      label={t('form:actions.confirm')}
    />
  )
}
